import AdminLayout from './AdminLayout';
import { useCachedGet } from '@/lib/cachedGet';
import { useAuth } from '@/contexts/AuthContext';
import { Users, BookOpen, Flame, IndianRupee, CalendarCheck, HandCoins, BedDouble, Newspaper, Eye } from 'lucide-react';

export default function AdminDashboard() {
  const { user } = useAuth();
  // A counter-linked account only ever gets counter-scoped booking/revenue
  // figures back from /admin/stats (see admin_stats in the backend) -
  // devotees, sevas, donations, accommodation and visitors aren't a
  // per-counter concept at all, so those cards would just always read 0
  // rather than mean anything for this login.
  const isCounterAccount = !!user?.counter_id;
  const { data: stats, loading } = useCachedGet('/admin/stats');

  const allCards = stats ? [
    { icon: Users, label: 'Total Devotees', value: stats.total_devotees, color: 'text-[#C43E00]', bg: 'bg-[#C43E00]/10' },
    { icon: BookOpen, label: 'Total Bookings', value: stats.total_bookings, color: 'text-[#621B00]', bg: 'bg-[#621B00]/10' },
    { icon: CalendarCheck, label: "Today's Bookings", value: stats.today_bookings, color: 'text-[#D4AF37]', bg: 'bg-[#D4AF37]/10' },
    { icon: Flame, label: 'Active Sevas', value: stats.total_sevas, color: 'text-[#C43E00]', bg: 'bg-[#C43E00]/10' },
    { icon: IndianRupee, label: 'Seva Revenue', value: `Rs. ${stats.total_revenue.toLocaleString()}`, color: 'text-[#621B00]', bg: 'bg-[#D4AF37]/10' },
    { icon: HandCoins, label: 'Total Donations', value: stats.total_donations, color: 'text-[#D4AF37]', bg: 'bg-[#D4AF37]/10' },
    { icon: IndianRupee, label: 'Donation Amount', value: `Rs. ${stats.total_donation_amount.toLocaleString()}`, color: 'text-green-600', bg: 'bg-green-50' },
    { icon: BedDouble, label: 'Accommodation Bookings', value: stats.total_acc_bookings, color: 'text-[#621B00]', bg: 'bg-[#621B00]/10' },
    { icon: Users, label: 'Total Visitors', value: stats.total_visitors?.toLocaleString(), color: 'text-[#C43E00]', bg: 'bg-[#C43E00]/10' },
    { icon: Eye, label: "Today's Visitors", value: stats.todays_visitors?.toLocaleString(), color: 'text-[#D4AF37]', bg: 'bg-[#D4AF37]/10' },
  ] : [];
  const counterOnlyLabels = ['Total Bookings', "Today's Bookings", 'Seva Revenue'];
  const cards = isCounterAccount ? allCards.filter(c => counterOnlyLabels.includes(c.label)) : allCards;

  return (
    <AdminLayout title="Dashboard">
      {loading ? <p className="text-[#8D6E63]">Loading stats...</p> : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
          {cards.map((c, i) => (
            <div key={i} className="bg-white border border-[#E6DCCA] rounded-xl p-3 sm:p-5 hover:border-[#D4AF37]/50 transition-all min-w-0" data-testid={`stat-card-${i}`}>
              <div className="flex items-center gap-2 sm:gap-3">
                <div className={`w-8 h-8 sm:w-10 sm:h-10 shrink-0 ${c.bg} rounded-full flex items-center justify-center`}>
                  <c.icon className={`h-5 w-5 ${c.color}`} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-[#8D6E63]">{c.label}</p>
                  <p className="text-lg sm:text-xl font-bold text-[#2D1B0E] break-words">{c.value}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}
